import React, {useEffect,useState} from "react";
import ProductHeader from "./ProductHeader";
import "./exitAssistant.css";

const value=n=>n==null?"—":Number(n).toLocaleString("en-IN",{maximumFractionDigits:2});
const words=s=>String(s||"Unavailable").replaceAll("_"," ");
const when=s=>s?new Date(s).toLocaleString("en-IN",{timeZone:"Asia/Kolkata",day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit",second:"2-digit",hour12:false}):"—";
function groupEntries(positions) {
  const groups = new Map();
  for (const fill of [...positions].reverse()) {
    const key = fill.group_id != null
      ? JSON.stringify([fill.group_id, fill.instrument, fill.contract, fill.source])
      : fill.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(fill);
  }
  return [...groups.entries()];
}

function Comparison({fills, select, children, perFill=false}) {
  const results = new Map();
  for (const fill of fills) {
    const result = select(fill);
    const key = JSON.stringify(result);
    if (!results.has(key)) results.set(key, {result, clients: new Set()});
    results.get(key).clients.add(fill.client_id);
  }
  if (results.size === 1) return <>{perFill&&fills.length>1&&<small>Per fill</small>}{children(results.values().next().value.result)}</>;
  return <details><summary>{results.size} different results</summary>
    {[...results].map(([key, {result, clients}])=><div key={key}>
      <small>{[...clients].join(", ")}</small>{children(result)}
    </div>)}
  </details>;
}

function variantSummary(data) {
  if (!data) return null;
  const {status, stop, exit, gross_pnl, reason, execution_error, exit_at} = data;
  return {status, stop, exit, gross_pnl, reason, execution_error, exit_at};
}

function LiveResult({fill}) {
  return fill.live_result_error
    ? <span className="health-caution">{fill.live_result_error}</span>
    : fill.live_exit_quantity
      ? <><div>{fill.live_exit_quantity} / {fill.quantity} units exited</div><strong>Gross Rs {value(fill.live_gross_pnl)}</strong></>
      : "Awaiting confirmed Live exit";
}

function EntryRow({fills}) {
  const p = fills[0];
  const clients = new Set(fills.map(fill=>fill.client_id));
  const quantity = fills.reduce((sum,fill)=>sum + Number(fill.quantity), 0);
  const samePremium = fills.every(fill=>fill.entry===p.entry);
  const entry = samePremium ? p.entry : fills.reduce((sum,fill)=>sum + fill.quantity * fill.entry, 0) / quantity;
  const multiple = fills.length > 1;
  return <tr role="row">
    <td role="cell" data-label="Contract / Live entry"><strong>{p.instrument} · {p.side}</strong><div>{p.contract}</div><small>{p.source}</small>
      <div>{samePremium?"Live entry premium":"Weighted average Live entry premium"} ₹{value(entry)}</div>
      <div>{quantity} units{multiple&&` across ${clients.size} ${clients.size===1?"client":"clients"}`}</div>
      {multiple?<details><summary>Live fills</summary>{fills.map(fill=><div key={fill.id}>
        <small>{fill.client_id} · {fill.quantity} units · ₹{value(fill.entry)}</small>
        <small>Live fill {when(fill.live_fill_at)} IST</small>
        <small>Shadow observed {when(fill.observed_at)} IST</small>
        {fill.fill_reconciliation_error&&<small className="health-caution">{fill.fill_reconciliation_error}</small>}
      </div>)}</details>:<><small>{p.client_id}</small><small>Live fill {when(p.live_fill_at)} IST</small><small>Shadow observed {when(p.observed_at)} IST</small></>}
    </td>
    <td role="cell" data-label="Market / recommendation"><Comparison fills={fills} select={({recommendation,reason,spot,mark,risk,spot_anchor_source,quote_error})=>({recommendation,reason,spot,mark,risk,spot_anchor_source,quote_error})}>
      {market=><><strong className={`exit-advice-${market.recommendation?.toLowerCase()}`}>{words(market.recommendation)}</strong><div>{market.reason}</div>
        <small>Spot {value(market.spot)} · option bid {value(market.mark)}</small><small>Initial spot risk {value(market.risk)} · {words(market.spot_anchor_source)}</small>
        {market.quote_error&&<small className="health-caution">{words(market.quote_error)}</small>}</>}
    </Comparison></td>
    <td role="cell" data-label="Actual Live result"><Comparison fills={fills} perFill select={({live_result_error,live_exit_quantity,quantity,live_gross_pnl})=>({live_result_error,live_exit_quantity,quantity,live_gross_pnl})}>
      {result=><LiveResult fill={result}/>}
    </Comparison></td>
    {["structure_5m","adaptive_1m"].map(name=><td role="cell" key={name} data-label={name==="structure_5m"?"5-minute structure · Paper":"Adaptive 1-minute · Paper"}><Comparison fills={fills} perFill select={fill=>variantSummary(fill.variants?.[name])}>
      {result=><Variant data={result}/>}
    </Comparison></td>)}
    <td role="cell" data-label="5-minute structure · Paper"><Comparison fills={fills} perFill={fills.some(fill=>fill.models_status==="RESEARCH")} select={fill=>fill.models_status==="RESEARCH" ? {
      variant:variantSummary(fill.variants?.learned), score:fill.research_score, version:fill.model_version
    } : null}>
      {result=>result?<><Variant data={result.variant}/><small>Research score {value(result.score)} · uncalibrated</small><small>{result.version}</small></>:"Not trained · baseline observations only"}
    </Comparison></td>
  </tr>;
}

function Variant({data}) {
  if (!data)return <>—</>;
  return <><strong>{words(data.status)}</strong><div>Protection {value(data.stop)}</div>
    {data.exit!=null&&<div>Exit premium {value(data.exit)} · gross Rs {value(data.gross_pnl)}</div>}
    <small>{data.reason?words(data.reason):"Following confirmed price structure"}</small>
    {data.execution_error&&<small className="health-caution">{words(data.execution_error)}</small>}
    {data.exit_at&&<small>{when(data.exit_at)} IST</small>}</>;
}

export default function ExitAssistant() {
  const [data,setData]=useState(null),[error,setError]=useState(""),[busy,setBusy]=useState(false);
  useEffect(()=>{
    const controller=new AbortController();let timer;
    const poll=async()=>{
      try {
        const response=await fetch("/api/exit-assistant",{signal:controller.signal,cache:"no-store"});
        const body=await response.json();
        if (!response.ok)throw Error(body.detail||"Exit Assistant unavailable");
        if (!controller.signal.aborted){setData(body);setError("");}
      } catch(e){if (!controller.signal.aborted)setError(e.message);}
      finally{if (!controller.signal.aborted)timer=setTimeout(poll,2500);}
    };
    poll();return()=>{controller.abort();clearTimeout(timer);};
  },[]);
  const action=async()=>{
    setBusy(true);
    try {
      const response=await fetch(`/api/exit-assistant/${data.enabled?"pause":"start"}`,{method:"POST"});
      const body=await response.json();
      if (!response.ok)throw Error(body.detail||"Unable to update collection");
      setData(body);setError("");
    } catch(e){setError(e.message);}finally{setBusy(false);}
  };
  return <section className="panel exit-assistant"><ProductHeader title="Exit Assistant" mode="paper" paperOnly status="Paper exits linked to confirmed Live Nifty / Sensex fills"/>
    <div className="panel-body"><p>Compare exit rules on the same Live entries. Live protection continues independently. This assistant cannot send broker orders.</p>
      <div className="exit-assistant-toolbar"><button disabled={!data||busy||Boolean(error)} onClick={action}>{busy?"Updating…":data?.enabled?"Pause new shadows":"Start paper collection"}</button><span role="status">{data?.enabled?"Collecting new confirmed Live fills":"New-fill collection paused"}</span></div>
      {(error||data?.error)&&<p role="alert">{error||data.error}</p>}
      <div className="exit-assistant-models">{["NIFTY","SENSEX"].map(s=><div key={s}><strong>{s}</strong><span>{data?.model_status?.[s]==="RESEARCH_MODEL"?"Trained research model · automatic Paper exits":"Rules baseline · collecting training data"}</span>
        {data?.learning?.instruments?.[s]&&<><small>{words(data.learning.instruments[s].status)}</small><small>{data.learning.instruments[s].reason}</small><small>{data.learning.instruments[s].labelled_trades??0} labelled trades · {data.learning.instruments[s].labelled_days??0} days</small></>}
      </div>)}</div>
      <p>Automatic learning runs after 16:00 IST on weekdays while collection is active. Qualified models apply to new Paper shadows; existing simulations keep their original model. Live automatic exits remain disabled.</p>
      <p>Collection watches new fills; existing entries are not recreated. Pausing stops new shadows while existing simulations continue. Results are gross; charges and net P&amp;L are unavailable.</p>
      <div className="positions-table-scroll"><table className="exit-assistant-table mobile-record-table" role="table" aria-label="Live entry paper exit comparison"><thead><tr role="row"><th role="columnheader" scope="col">Contract / Live entry</th><th role="columnheader" scope="col">Market / recommendation</th><th role="columnheader" scope="col">Actual Live result</th><th role="columnheader" scope="col">5-minute structure · Paper</th><th role="columnheader" scope="col">Adaptive 1-minute · Paper</th><th role="columnheader" scope="col">Learned model · Paper</th></tr></thead><tbody>
        {groupEntries(data?.positions||[]).map(([key,fills])=><EntryRow key={key} fills={fills}/>)}{!data?.positions?.length&&<tr role="row"><td role="cell" colSpan="6">No confirmed Live fills mirrored yet. Start collection before the next Live entry.</td></tr>}
      </tbody></table></div>
      <details><summary>Paper exit rules and data quality</summary><p>Initial protection uses a pre-entry confirmed 5-minute swing. Swings need two completed candles on each side. Adaptive protection activates at +1R, tests a +0.25R index-profit floor, and follows confirmed 1-minute structure. These are research parameters, not validated optimal settings.</p><p>Spot is anchored to the first fresh observation after the broker fill; its time is recorded. Late attachments and missing structure are unavailable. Paper exits require fresh option bid depth. Missing fills remain pending or unpriced. Session square-off is 15:12 IST. Quote collection continues after Live and Paper exits for research labels.</p><p>Learned models are validated separately per index. Scores are research signals, not calibrated probabilities. No Live exit activation is available.</p></details>
    </div></section>;
}
